import React, { useState } from 'react';
import { CodeIcon, FlaskConicalIcon, SettingsIcon, LayoutIcon, QuoteIcon } from 'lucide-react';
interface UseCase {
  id: string;
  label: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  quote: string;
  author: string;
  role: string;
}
const useCases: UseCase[] = [{
  id: 'engineering',
  label: 'Engineering',
  icon: <CodeIcon className="w-5 h-5" />,
  title: 'Parallelize your development',
  description: 'Run multiple workstreams simultaneously. Research, implementation, and testing happen in parallel while the graph tracks dependencies. Context switches become instant.',
  quote: 'We shipped a feature in 3 days that would have taken 2 weeks. The graph showed us exactly where to parallelize.',
  author: 'Sarah Chen',
  role: 'Staff Engineer at Stripe'
}, {
  id: 'research',
  label: 'Research',
  icon: <FlaskConicalIcon className="w-5 h-5" />,
  title: 'Explore without losing track',
  description: 'Branch to test hypotheses, compare approaches side-by-side, and merge winning strategies back. Every experiment is documented automatically.',
  quote: 'Finally, a tool that matches how research actually works. Non-linear, exploratory, but still organized.',
  author: 'Dr. James Liu',
  role: 'Research Lead at DeepMind'
}, {
  id: 'ops',
  label: 'Ops',
  icon: <SettingsIcon className="w-5 h-5" />,
  title: 'Automate the routine',
  description: 'Set up polling tasks that monitor systems, trigger responses, and escalate only when needed. Your runbooks become executable graphs.',
  quote: 'Our incident response time dropped 60%. The graph handles triage while we focus on resolution.',
  author: 'Mike Rodriguez',
  role: 'SRE Manager at Datadog'
}, {
  id: 'product',
  label: 'Product',
  icon: <LayoutIcon className="w-5 h-5" />,
  title: 'Ship with full context',
  description: 'From research to launch, every decision is captured. Generate PRDs, stakeholder updates, and launch docs from your execution history.',
  quote: 'Stakeholder updates used to take hours. Now they generate themselves from our work graph.',
  author: 'Emily Park',
  role: 'Product Lead at Notion'
}];
export function UseCases() {
  const [activeCase, setActiveCase] = useState(useCases[0]);
  return <section className="py-32 px-6 bg-surface-800/50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-primary-400 font-medium mb-4">Use Cases</p>
          <h2 className="text-4xl font-bold text-white mb-6">
            Built for how teams actually work
          </h2>
        </div>

        {/* Tab navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-surface-700 rounded-xl p-1.5">
            {useCases.map(useCase => <button key={useCase.id} onClick={() => setActiveCase(useCase)} className={`
                  flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all
                  ${activeCase.id === useCase.id ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25' : 'text-gray-400 hover:text-white'}
                `}>
                {useCase.icon}
                {useCase.label}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Mockup */}
          <div className="relative">
            <div className="bg-surface-700 rounded-2xl border border-surface-500 p-6 shadow-2xl">
              {/* Mock header */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-danger-500" />
                <div className="w-3 h-3 rounded-full bg-warning-500" />
                <div className="w-3 h-3 rounded-full bg-success-500" />
                <span className="ml-4 text-sm text-gray-400">
                  StreamGraph — {activeCase.label}
                </span>
              </div>
              {/* Mock content */}
              <div className="h-64 bg-surface-800 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-500/20 flex items-center justify-center">
                    <div className="text-primary-400">{activeCase.icon}</div>
                  </div>
                  <p className="text-gray-400 text-sm">{activeCase.title}</p>
                </div>
              </div>
            </div>
            {/* Decorative glow */}
            <div className="absolute -inset-4 bg-primary-500/10 rounded-3xl blur-2xl -z-10" />
          </div>

          {/* Text content */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-4">
                {activeCase.title}
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                {activeCase.description}
              </p>
            </div>

            {/* Testimonial */}
            <div className="bg-surface-700 rounded-xl p-6 border border-surface-500">
              <QuoteIcon className="w-8 h-8 text-primary-500/50 mb-4" />
              <p className="text-white text-lg mb-4 italic">
                "{activeCase.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-purple-500" />
                <div>
                  <p className="text-white font-medium">{activeCase.author}</p>
                  <p className="text-gray-400 text-sm">{activeCase.role}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}